// Welcome modal shown on first visit
// Appears immediately and auto-dismisses after 10s (pauses on hover/touch). Includes countdown and dismiss/disable options.
import { useEffect, useState, useCallback } from 'react';
import { useLocation } from 'react-router-dom';
import { setAnalyticsConsent } from '../utils/analytics';

const WELCOME_STORAGE_KEY = 'om_welcome_dismissed_v1';
const AUTO_DISMISS_MS = 10000;

export function WelcomeModal() {
  const [visible, setVisible] = useState(false);
  const [dismissed, setDismissed] = useState(false);
  const [remainingMs, setRemainingMs] = useState(AUTO_DISMISS_MS);
  const [paused, setPaused] = useState(false);
  const location = useLocation();

  // Show immediately on first visit only
  useEffect(() => {
    try {
      const alreadyDismissed = localStorage.getItem(WELCOME_STORAGE_KEY) === 'true';
      setDismissed(alreadyDismissed);
      if (!alreadyDismissed) setVisible(true);
    } catch {
      setVisible(true);
    }
    return;
  }, []);

  // Start auto-dismiss countdown (temporary hide only)
  useEffect(() => {
    if (!visible || dismissed) return;
    let intervalId: number | undefined;
    const tick = () => {
      setRemainingMs((prev) => {
        if (paused) return prev;
        const next = prev - 100;
        if (next <= 0) {
          // Auto-hide when timer completes (do not persist dismissal)
          handleAutoHide();
          return 0;
        }
        return next;
      });
    };
    intervalId = window.setInterval(tick, 100);
    return () => {
      if (intervalId) window.clearInterval(intervalId);
    };
  }, [visible, dismissed, paused]);

  // Temporary hide without persisting dismissal
  const handleAutoHide = useCallback(() => {
    setVisible(false);
  }, []);

  // Dismiss handler
  const handleDismiss = useCallback(() => {
    try {
      localStorage.setItem(WELCOME_STORAGE_KEY, 'true');
    } catch {
      // Ignore storage errors
    }
    setVisible(false);
    setDismissed(true);
  }, []);

  // Disable analytics and dismiss
  const handleDisableAnalytics = useCallback(() => {
    setAnalyticsConsent(false);
    handleDismiss();
  }, [handleDismiss]);

  // Close on Escape
  useEffect(() => {
    if (!visible) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') handleAutoHide();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [visible, handleAutoHide]);

  // Re-show on route change if not accepted/disabled
  useEffect(() => {
    if (dismissed) return;
    setRemainingMs(AUTO_DISMISS_MS);
    setPaused(false);
    setVisible(true);
  }, [location.pathname, dismissed]);

  if (!visible) return null;

  return (
    <div className="fixed inset-x-0 bottom-0 z-[70] flex items-end justify-center pointer-events-none">
      {/* Toast card - small and unobtrusive at bottom-center */}
      <div
        className="relative m-4 w-full max-w-sm bg-zinc-900/95 border border-zinc-800 rounded-2xl shadow-xl pointer-events-auto"
        role="status"
        aria-live="polite"
        onMouseEnter={() => setPaused(true)}
        onMouseLeave={() => setPaused(false)}
        onTouchStart={() => setPaused(true)}
        onTouchEnd={() => setPaused(false)}
      >
        <div className="p-4">
          <div className="flex items-start gap-3">
            <div className="w-9 h-9 rounded-lg bg-cyan-500/15 border border-cyan-500/30 flex items-center justify-center">
              <svg className="w-5 h-5 text-cyan-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 20l-5.447-2.724A1 1 0 013 16.382V5.618a1 1 0 011.447-.894L9 7m0 13l6-3m-6 3V7m6 10l4.553 2.276A1 1 0 0021 18.382V7.618a1 1 0 00-.553-.894L15 4m0 13V4m0 0L9 7" />
              </svg>
            </div>
            <div className="flex-1">
              <h3 className="text-sm font-semibold text-white">Welcome to Open-Me.sh</h3>
              <p className="mt-1 text-xs text-zinc-400">
                Explore mesh networking concepts interactively. Use the Controls and Legend in the header, or try the Playground for a full-screen view.
              </p>
              <p className="mt-3 text-[11px] text-zinc-500">
                Anonymous usage analytics help improve this experience. You can disable analytics anytime from the footer or below. See our Privacy page for details.
              </p>
            </div>
          </div>

          {/* Countdown progress + actions */}
          <div className="mt-3">
            <div className="h-1.5 bg-zinc-800 rounded-full overflow-hidden">
              <div
                className={`h-full ${paused ? 'bg-zinc-600' : 'bg-cyan-500/70'} transition-[width]`}
                style={{ width: `${Math.max(0, (remainingMs / AUTO_DISMISS_MS) * 100)}%` }}
                aria-hidden="true"
              />
            </div>
            <div className="mt-1 text-[10px] text-zinc-500">
              {paused ? 'Paused' : `Closing in ${Math.ceil(remainingMs / 1000)}s`}
            </div>
          </div>

          <div className="mt-4 flex items-center justify-between gap-2">
            <a
              href="/privacy"
              className="text-[11px] text-cyan-400 hover:text-cyan-300 underline"
            >
              Privacy
            </a>
            <div className="flex items-center gap-2">
              <button
                onClick={handleDisableAnalytics}
                className="px-3 py-1.5 text-[11px] rounded-lg border border-zinc-700 text-zinc-300 hover:bg-zinc-800"
              >
                Disable analytics
              </button>
              <button
                onClick={handleDismiss}
                className="px-3 py-1.5 text-[11px] rounded-lg bg-cyan-500/20 text-cyan-300 border border-cyan-500/30 hover:bg-cyan-500/30"
              >
                Got it
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}


